import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslations } from 'src/contexts/TranslationContext';
import categoriesData from 'src/backendshared/xnews_categories.json';
import { formatCategoryName } from 'src/utils/categoryFormatter';
import {PageTitleDescriptionHelmet} from '../../components/seo/NewsPageHelmets';


const rawCategories: Record<string, string[]> = categoriesData;

// Exclude 'main' and 'trending', lowercase and sort
const categories: string[] = Object.keys(rawCategories)
  .filter(c => c.toLowerCase() !== 'main' && c.toLowerCase() !== 'trending')
  .map(c => c.toLowerCase())
  .sort();

// Generate year-month strings starting at 2025-04
const generateYearMonths = (startYear: number, startMonth: number): string[] => {
  const result: string[] = [];
  const now = new Date();
  const endYear = now.getMonth() === 0 ? now.getFullYear() - 1 : now.getFullYear();
  const endMonth = now.getMonth() === 0 ? 12 : now.getMonth();

  let y = startYear;
  let m = startMonth;

  while (y < endYear || (y === endYear && m <= endMonth)) {
    const mm = m < 10 ? '0' + m : m.toString();
    result.push(`${y}${mm}`);
    m++;
    if (m > 12) {
      m = 1;
      y++;
    }
  }
  return result;
};

const Archive: React.FC = () => {

  const { lang } = useParams<{ lang: string }>();
  const navigate = useNavigate();
  const { language, getUrlLanguagePrefix, translations: t } = useTranslations();

  const yearMonths = generateYearMonths(2025, 4);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  // Navigation handlers
  const onCategoryClick = (category: string) => {
    const prefix = getUrlLanguagePrefix(language);
    navigate(`${prefix}/news/archive/category/${category}`);
  };

  const onMonthClick = (yyyymm: string) => {
    if (!selectedCategory) return;
    const prefix = getUrlLanguagePrefix(language);
    navigate(`${prefix}/news/archive/category/${selectedCategory}/${yyyymm}`);
  };

  const formatMonthLabel = (yyyymm: string) => {
    const year = parseInt(yyyymm.substring(0, 4), 10);
    const month = parseInt(yyyymm.substring(4, 6), 10);
    const date = new Date(year, month - 1);
    return date.toLocaleString(language || 'en', { year: 'numeric', month: 'long' });
  };

  // Generate translated category list for meta description
  const translatedCategoryList = categories
    .map(c => t?.navigations?.header?.[c] || formatCategoryName(c))
    .join(', ');

  return (
    <div className="max-w-5xl mx-auto p-6">


      <PageTitleDescriptionHelmet
        title={t?.navigations?.archive?.title}
        description={
          `${t?.navigations?.archive?.archivemonth?.metadescription || 'News Archive'} — ${translatedCategoryList}`
        }
      />


      <h1 className="text-3xl font-bold mb-6 text-center">
        {t?.navigations?.archive?.title || 'News Archive'}
      </h1>

      {!selectedCategory ? (
        <>
          <p className="mb-4 text-center text-gray-700">
            {t?.archive?.selectCategory || 'Select a category to browse archive'}
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {categories.map(category => (
              <button
                key={category}
                onClick={() => onCategoryClick(category)}
                className="py-3 px-4 border rounded-md text-center font-medium text-gray-800 hover:bg-gray-100 transition"
              >
                {t?.navigations?.header?.[category] || formatCategoryName(category)}
              </button>
            ))}
          </div>
        </>
      ) : (
        <>
          <div className="mb-4 flex items-center space-x-4">
            <button
              onClick={() => setSelectedCategory(null)}
              className="text-blue-600 hover:underline"
            >
              ← {t?.archive?.backToCategories || 'Back to Categories'}
            </button>
            <h2 className="text-xl font-semibold capitalize">
              {t?.navigations?.header?.[selectedCategory] || formatCategoryName(selectedCategory)}
            </h2>
          </div>

          <p className="mb-4 text-gray-700">
            {t?.archive?.selectMonth || 'Select a month to view news'}
          </p>

          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3">
            {yearMonths.map(yyyymm => (
              <button
                key={yyyymm}
                onClick={() => onMonthClick(yyyymm)}
                className="py-2 px-3 border rounded-md text-center hover:bg-blue-50 hover:border-blue-300 transition text-sm"
              >
                {formatMonthLabel(yyyymm)}
              </button>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Archive;
